// Friendly labels + grouping for credit transactions.
//
// The backend stores ``reason`` as a dotted operation path
// (e.g. ``inference.predictfm_ttsi``, ``ingestion.bulk_per_gb``).
// This module turns those into human-readable column values:
//
//   {
//     label:    "Inference (TTSI)",
//     category: "Inference",
//     icon:     "Bolt",
//     tone:     "primary",
//   }
//
// Used by the Billing page's Activity tab + the Spend chart's
// per-category aggregation.

// Categories where credits FLOW OUT of the user's balance.
// The spend chart + donut + activity-filter spend views use this list.
export const SPEND_CATEGORIES = [
  'Inference', 'Training', 'Hosting', 'Storage',
  'Ingestion', 'BYOM', 'Eval', 'Other',
];

// Categories where credits FLOW INTO the balance (grants, paid top-ups,
// refunds). They appear in the Activity table but never in spend views.
export const ADDITION_CATEGORIES = ['Grants', 'Top-ups'];

// Full set used by the activity filter dropdown.
export const CATEGORIES = [...ADDITION_CATEGORIES, ...SPEND_CATEGORIES];

export const CATEGORY_COLORS = {
  Grants:    '#22c55e',
  'Top-ups': '#10b981',
  Inference: '#3b82f6',
  Training:  '#8b5cf6',
  Hosting:   '#f97316',
  Storage:   '#f59e0b',
  Ingestion: '#06b6d4',
  BYOM:      '#ec4899',
  Eval:      '#84cc16',
  Other:     '#94a3b8',
};

// True if a category represents money LEAVING the balance.
export const isSpendCategory = (cat) => SPEND_CATEGORIES.includes(cat);

// Pattern -> { category, label } mapping. First match wins.
//
// ``signup_bonus`` and ``monthly_grant`` rules are retained so older
// transaction rows from before the credit-grant subsystem was
// removed still render with a friendly label - they'll never appear
// on new accounts, but historical statements need to read cleanly.
const RULES = [
  { test: /^signup_bonus$/i,             label: 'Signup bonus (legacy)', category: 'Grants' },
  { test: /^monthly_grant$/i,            label: 'Monthly grant (legacy)', category: 'Grants' },
  { test: /^manual_topup$/i,             label: 'Top-up',                category: 'Top-ups' },
  { test: /^refund:/i,                   label: 'Refund',                category: 'Grants' },

  { test: /^inference\.predictfm_ttsi/i,    label: 'Foundation — predictfm (TTSI)', category: 'Inference' },
  { test: /^inference\.predictfm_no_ttsi/i, label: 'Foundation — predictfm',        category: 'Inference' },
  { test: /^inference\.triton_dedicated/i,  label: 'Custom (dedicated)', category: 'Inference' },
  { test: /^inference\.triton_shared/i,     label: 'Custom (shared)',   category: 'Inference' },
  { test: /^inference\.byom_dedicated/i,    label: 'BYOM (dedicated)',  category: 'Inference' },
  { test: /^inference\.byom_shared/i,       label: 'BYOM (shared)',     category: 'Inference' },
  { test: /^inference\.external_fm_(.+)/i,  label: (m) => `Foundation — ${m[1].replace(/_/g, ' ')}`, category: 'Inference' },
  { test: /^inference\./i,                  label: 'Inference',         category: 'Inference' },

  { test: /^adapter_training\.adapt$/i,      label: 'Adapter (adapt)',      category: 'Training' },
  { test: /^adapter_training\.specialize$/i, label: 'Adapter (specialize)', category: 'Training' },
  { test: /^adapter_training\.forge$/i,      label: 'Adapter (forge)',      category: 'Training' },
  { test: /^external_fm_training\.([^.]+)\.(.+)/i,
    label: (m) => `Foundation fine-tune (${m[1].replace(/_/g, ' ')} - ${m[2]})`,
    category: 'Training' },
  { test: /^custom_training\.cpu_per_second/i, label: 'Custom training (CPU)', category: 'Training' },
  { test: /^custom_training\.gpu_per_second/i, label: 'Custom training (GPU)', category: 'Training' },
  { test: /^storage\.gb_day_hot/i,   label: 'Storage (hot)',     category: 'Storage' },
  { test: /^storage\.gb_day_warm/i,  label: 'Storage (warm)',    category: 'Storage' },
  { test: /^storage\.gb_day_cold/i,  label: 'Storage (cold)',    category: 'Storage' },
  // Legacy per-hour storage rows from the pre-daily worker remain in
  // the audit log; keep these patterns so the activity table still
  // renders them as Storage rather than the "Other" fallback.
  { test: /^storage\.gb_hour_hot/i,  label: 'Storage (hot)',     category: 'Storage' },
  { test: /^storage\.gb_hour_warm/i, label: 'Storage (warm)',    category: 'Storage' },
  { test: /^storage\.gb_hour_cold/i, label: 'Storage (cold)',    category: 'Storage' },
  { test: /^storage_monthly/i,       label: 'Storage',           category: 'Storage' },

  { test: /^hosting\.kserve_dedicated_per_day/i, label: 'Hosting (dedicated)',     category: 'Hosting' },
  { test: /^hosting\.modelmesh_shared_per_day/i, label: 'Hosting (shared)',        category: 'Hosting' },
  { test: /^hosting\.foundation_shared_per_day/i, label: 'Hosting (foundation)',   category: 'Hosting' },
  { test: /^hosting\./i,                          label: 'Hosting',                category: 'Hosting' },

  { test: /^ingestion\.bulk_per_gb/i,            label: 'Bulk upload',         category: 'Ingestion' },
  { test: /^ingestion\.streaming_per_thousand/i, label: 'Streaming events',    category: 'Ingestion' },
  { test: /^ingestion\.connector_orchestration/i, label: 'Connector run',     category: 'Ingestion' },
  { test: /^ingestion\./i,                        label: 'Data ingestion',     category: 'Ingestion' },

  { test: /^byom\.upload_per_mb/i, label: 'BYOM upload',   category: 'BYOM' },
  { test: /^byom\.validate/i,      label: 'BYOM validate', category: 'BYOM' },

  { test: /^eval\.backbone_full_suite/i, label: 'Eval (full suite)',   category: 'Eval' },
  { test: /^eval\.adapter_quick_eval/i,  label: 'Eval (quick)',        category: 'Eval' },
  { test: /^eval\.mia_score/i,           label: 'Eval (MIA)',          category: 'Eval' },
];

/**
 * Translate a raw operation/reason string into a friendly
 * { label, category } pair.
 */
export const describeActivity = (reason) => {
  if (!reason) return { label: 'Activity', category: 'Other' };
  for (const rule of RULES) {
    const match = reason.match(rule.test);
    if (match) {
      const label = typeof rule.label === 'function' ? rule.label(match) : rule.label;
      return { label, category: rule.category };
    }
  }
  // Fallback: prettify the dotted path (replace dots/underscores with spaces).
  return {
    label: reason.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    category: 'Other',
  };
};

/**
 * Filter options for the Activity tab. The first item is "All", the
 * rest are categories with at least one matching rule.
 */
export const FILTER_OPTIONS = [
  { id: '', label: 'All activity' },
  ...CATEGORIES.map((c) => ({ id: c, label: c })),
];

/**
 * Format credits with a thin space + "credits" suffix. ``compact``
 * uses k/M abbreviation for the top-bar widget.
 */
export const formatCredits = (n, { compact = false } = {}) => {
  if (n == null || isNaN(n)) return '0';
  const v = Number(n);
  if (compact) {
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
    return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/**
 * Bytes -> human (used for ingest/storage gauges).
 */
export const formatBytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 ** 2) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 ** 3) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  if (v < 1024 ** 4) return `${(v / 1024 ** 3).toFixed(1)} GB`;
  return `${(v / 1024 ** 4).toFixed(1)} TB`;
};

/**
 * Quota meter colour: green when comfortably under, amber over 75%,
 * red over 100%.
 */
export const meterTone = (used, included) => {
  if (!included) return 'success';
  const pct = used / included;
  if (pct >= 1) return 'error';
  if (pct >= 0.75) return 'warning';
  return 'success';
};

/**
 * "X of Y" label for a hard-quota row, with -1 = unlimited handling.
 */
export const formatLimitUsage = (used, limit) => {
  if (limit === -1 || limit == null) return `${(used ?? 0).toLocaleString()} (unlimited)`;
  return `${(used ?? 0).toLocaleString()} of ${limit.toLocaleString()}`;
};

/**
 * Compute the percentage filled for a quota bar; clamps to [0,100]
 * and treats unlimited (-1) as zero so the bar stays empty.
 */
export const limitPercent = (used, limit) => {
  if (limit === -1 || limit == null || limit <= 0) return 0;
  return Math.min(100, Math.max(0, ((used ?? 0) / limit) * 100));
};

export const tierLabel = (tier) => {
  if (!tier) return 'Free';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
};
